/**
 * RatingBadges — tiny rating-source logos for the top-left of a poster.
 *
 * Each badge is a small brand-coloured mark + the value, so you can glance at a
 * card and see which ratings it has (IMDb / Rotten Tomatoes / Metacritic / TMDB).
 * Metacritic uses its score-band colour (green ≥61, yellow 40–60, red <40),
 * mirroring how Metacritic itself colours the metascore.
 */
import type { RatingValue } from "@hyggebam/shared";
import type React from "react";

function metacriticColor(score: number | null): string {
  if (score == null) return "#6c6c6c";
  if (score >= 61) return "#66cc33";
  if (score >= 40) return "#ffbd3f";
  return "#ff6874";
}

function mark(r: RatingValue): { cls: string; text: string; style?: React.CSSProperties } {
  switch (r.source) {
    case "IMDb":
      return { cls: "imdb", text: "IMDb" };
    case "Rotten Tomatoes":
      return { cls: "rt", text: "🍅" };
    case "Metacritic":
      return { cls: "mc", text: "M", style: { background: metacriticColor(r.score) } };
    default:
      return { cls: "tmdb", text: "TMDB" };
  }
}

export function RatingBadges({ ratings }: { ratings: RatingValue[] }) {
  if (!ratings || ratings.length === 0) return null;
  return (
    <div className="poster-ratings" data-testid="card-ratings">
      {ratings.map((r) => {
        const m = mark(r);
        return (
          <div key={r.source} className="rt-chip" data-testid="card-rating" data-source={r.source}>
            <span className={`rt-mark ${m.cls}`} style={m.style} aria-hidden="true">
              {m.text}
            </span>
            <span className="rt-val">{r.display}</span>
          </div>
        );
      })}
    </div>
  );
}
